import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";


import BasePadding from "@cayuse/shared-react.components.base-padding";
import Breadcrumbs from "@cayuse/shared-react.components.breadcrumbs";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";
import SectionHeader from "@cayuse/shared-react.components.section-header";
import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";
import { Ref } from "semantic-ui-react";

import ProjectSummary from "components/ProjectSummary";
import RelatedRecords from "components/RelatedRecords";
import Tabs from "components/Tab/Tabs";
import ProjectAttachmentsView from "containers/Project/Detail/Attachments";
import ProjectAwardsView from "containers/Project/Detail/Awards";
import ProjectHistoryView from "containers/Project/Detail/History";
import ProjectNotesView from "containers/Project/Detail/Notes";
import ProjectProposalsView from "containers/Project/Detail/Proposals";
import ProjectSubawardsView from "containers/Project/Detail/Subawards";
import ProjectTasksView from "containers/Project/Detail/Tasks";
import { PROJECTS_BASE_ROUTE, IS_DEV, FORM_SUBMISSION_TYPES } from "globals/constants";
import useDictionary from "hooks/useDictionary";
import { getProject, resetProject } from "store/actions/project";
import { projectSelectors } from "store/selectors";
import { spUrl, findAwards, notifyError } from "utils";

import styles from "./styles.css";

const ProjectDetailView = () => {
	const dictionary = useDictionary("projectDetails");
	const params = useParams();

	const project = useSelector(projectSelectors.project);
	const isLoading = useSelector(projectSelectors.isFetching);

	const tabsRef = useRef(null);
	const [ awards, setAwards ] = useState([]);
	const [ isUploading, setIsUploading ] = useState(false); // todo: get from redux

	const breadcrumbs = (
		<Breadcrumbs breadcrumbs={[
			{
				key: 0,
				text: dictionary.breadcrumbRoot || "Project Dashboard",
				link: spUrl(PROJECTS_BASE_ROUTE)
			},
			{
				key: 2,
				text: project?.projectNumber
			}
		]}
		/>
	);

	const panes = [
		{
			menuItem: "Proposals",
			render: () => <ProjectProposalsView projectId={project.projectId} />,
			ref: useRef(null),
		},
		{
			menuItem: "Awards",
			render: () => <ProjectAwardsView projectId={project.projectId} />,
			ref: useRef(null),
		},
		{
			menuItem: "Subawards",
			render: () => <ProjectSubawardsView projectId={project.projectId} />,
			ref: useRef(null),
		},
		{
			menuItem: "History",
			render: () => <ProjectHistoryView projectId={project.projectId} />,
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.relatedRecords || "Related Records",
			render: () => <RelatedRecords formType={FORM_SUBMISSION_TYPES.PROJECT} />,
			ref: useRef(null),
		},
		{
			menuItem: "Tasks",
			render: () => <ProjectTasksView projectId={project.projectId} />,
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.notes || "Notes",
			render: () => <ProjectNotesView/>,
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.attachments || "Attachments",
			render: () => <ProjectAttachmentsView isUploading={isUploading} setIsUploading={setIsUploading} />,
			ref: useRef(null),
		}
	];

	useEffect(() => {
		const projectId = params.projectId;

		if (projectId !== project.projectId) {
			resetProject();
			getProject(projectId)
				.then(() =>{
					findAwards({
						pageLimit: 25,
						pageOffset: 0,
						searchTerms: { projectId },
						fieldKeys: [ "awardId" ]
					}
					).then(content => {setAwards(content.items);});
				})
				.catch(error => {
					const message = IS_DEV ? error.message : dictionary.error;
					notifyError(message, error);
				});
		}
	}, [ dictionary.error, params, project.projectId ]);

	return (
		<>
			<div className={styles.sectionHeaderWrapper}>
				{isLoading && (
					<div className={styles.sectionHeaderLoader}>
						<LoadingIndicator/>
					</div>
				)}
				<SectionHeader header={project.projectNumber && project.title && `${project.projectNumber}: ${project.title}`} breadcrumbs={breadcrumbs}/>
			</div>

			<BasePadding>
				{!isLoading && <ProjectSummary hasAwards={isNonEmptyArray(awards)} />}
				<Ref innerRef={tabsRef}>
					<Tabs
						className={styles.projectDetailsTabs}
						panes={panes}
						disabled={isUploading}
					/>
				</Ref>
			</BasePadding>
		</>
	);
};

export default ProjectDetailView;
